export type QuestObjective =
  | { id: string; type: 'kill'; monsterId: string; count: number; label: string }
  | { id: string; type: 'collect'; itemId: string; count: number; label: string }
  | { id: string; type: 'explore'; areaId: string; label: string }
  | { id: string; type: 'talk'; npcId: string; label: string };

export interface QuestReward {
  xp: number;
  coins: number;
  items: { itemId: string; qty: number }[];
}

export interface QuestDef {
  id: string;
  name: string;
  summary: string;
  giverNpcId: string;
  turnInNpcId: string;
  levelRequired: number;
  prerequisites: string[];
  /** false = once per character; 'daily' = may be repeated once per UTC day. */
  repeatable: false | 'daily';
  objectives: QuestObjective[];
  rewards: QuestReward;
  /** Items required by 'collect' objectives are removed when the quest is turned in. */
  consumeCollected: boolean;
  dialogue: { offer: string[]; inProgress: string; complete: string };
  /** Main storyline quests are listed first in the journal. */
  story: boolean;
}

export const QUESTS: Readonly<Record<string, QuestDef>> = {
  q_first_steps: {
    id: 'q_first_steps',
    name: 'A Disciple Arrives',
    summary: 'Elder Bai asks you to introduce yourself to Captain Wen at the eastern gate.',
    giverNpcId: 'elder_bai',
    turnInNpcId: 'guard_captain_wen',
    levelRequired: 1,
    prerequisites: [],
    repeatable: false,
    objectives: [
      { id: 'talk_wen', type: 'talk', npcId: 'guard_captain_wen', label: 'Speak with Captain Wen' },
    ],
    rewards: { xp: 100, coins: 10, items: [{ itemId: 'minor_healing_pill', qty: 2 }] },
    consumeCollected: false,
    dialogue: {
      offer: [
        'So, Wudang sent us another young sword. Good. Willow Creek has need of one.',
        'Before you go wandering, find Captain Wen at the eastern gate. He keeps the watch and knows every trouble on our roads.',
      ],
      inProgress: 'Captain Wen waits by the eastern gate. Follow the road past the well.',
      complete: 'Ah, the Elder’s new disciple. Your stance is steady — let us see if your sword is too.',
    },
    story: true,
  },
  q_northern_wolves: {
    id: 'q_northern_wolves',
    name: 'Wolves of the Northern Meadow',
    summary: 'Wolves have been stalking the herders north of the stream. Cross the bridge and thin the pack.',
    giverNpcId: 'guard_captain_wen',
    turnInNpcId: 'guard_captain_wen',
    levelRequired: 1,
    prerequisites: ['q_first_steps'],
    repeatable: false,
    objectives: [
      {
        id: 'reach_meadow',
        type: 'explore',
        areaId: 'northern_meadow',
        label: 'Cross the bridge to the Northern Meadow',
      },
      {
        id: 'kill_wolves',
        type: 'kill',
        monsterId: 'grey_wolf',
        count: 5,
        label: 'Defeat Grey Meadow Wolves',
      },
    ],
    rewards: { xp: 260, coins: 30, items: [{ itemId: 'pine_needle_sword', qty: 1 }] },
    consumeCollected: false,
    dialogue: {
      offer: [
        'The herders will not take their goats past the stream any more. A grey pack has moved into the meadow.',
        'Cross the stone bridge north of the well and drive them off. Five should teach the rest some manners.',
        'Use your footwork — press Shift to run and Space to sidestep their bites.',
      ],
      inProgress: 'The wolves still prowl the meadow. Five, disciple.',
      complete: 'The herders will sleep tonight. Take this — the Elder carried it when he was young.',
    },
    story: true,
  },
  q_ashes_eastern_road: {
    id: 'q_ashes_eastern_road',
    name: 'Ashes on the Eastern Road',
    summary:
      'Bandits raided the granary cart. Recover the stolen rice and break their camp east of the village.',
    giverNpcId: 'elder_bai',
    turnInNpcId: 'elder_bai',
    levelRequired: 2,
    prerequisites: ['q_northern_wolves'],
    repeatable: false,
    objectives: [
      {
        id: 'kill_bandits',
        type: 'kill',
        monsterId: 'mountain_bandit',
        count: 4,
        label: 'Defeat Mountain Bandits',
      },
      {
        id: 'rice',
        type: 'collect',
        itemId: 'stolen_rice_sack',
        count: 3,
        label: 'Recover Stolen Rice Sacks',
      },
    ],
    rewards: { xp: 450, coins: 60, items: [{ itemId: 'cloud_silk_robe', qty: 1 }] },
    consumeCollected: true,
    dialogue: {
      offer: [
        'Word came at dawn: the granary cart burned on the eastern road, and our autumn rice with it — or so they think.',
        'Bandits from the hills took the sacks to their camp. Bring back what was stolen and show them Willow Creek is not undefended.',
        'Beware the bigger ones. When a bandit raises his saber high, step out of the way.',
      ],
      inProgress: 'Their camp lies past the eastern gate, beyond the shrine. Bring back three sacks.',
      complete:
        'Rice for the winter and a lesson for the hills. The weavers made this robe for whoever returned our harvest.',
    },
    story: true,
  },
  q_daily_pelts: {
    id: 'q_daily_pelts',
    name: 'Pelts for the Winter Trade',
    summary: 'Herbalist Lin trades wolf pelts with the caravans. Bring her five.',
    giverNpcId: 'herbalist_lin',
    turnInNpcId: 'herbalist_lin',
    levelRequired: 1,
    prerequisites: [],
    repeatable: 'daily',
    objectives: [
      { id: 'pelts', type: 'collect', itemId: 'wolf_pelt', count: 5, label: 'Collect Wolf Pelts' },
    ],
    rewards: { xp: 80, coins: 25, items: [] },
    consumeCollected: true,
    dialogue: {
      offer: [
        'The caravans pay well for good pelts before the snows. Bring me five and I will share the profit.',
        'You may do this once each day — the traders only come by so often.',
      ],
      inProgress: 'Five pelts, no fewer. The meadow wolves have plenty.',
      complete: 'Thick and clean. Here is your share.',
    },
    story: false,
  },
};

export function getQuest(id: string): QuestDef | undefined {
  return QUESTS[id];
}
